import { createContext, forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useMemo, useRef } from "react";
import { FormAutofillProvider, useCheckAutofill } from "../hooks/autofillHook";
import useMessageDisplay from "./MessageDisplay";
import { useTranslation } from "react-i18next";

export const ReactFormContext = createContext({ onReset: () => { }, registerDependentForm: () => () => { }});

export const useReactForm = () => useContext(ReactFormContext);

/**
 * A special form-like element that supports nesting so that form validation can be used but nested forms ignored
 * @component 
 */
const ReactFormImpl = forwardRef(({ children, registerAsChild = false, ...props }, ref) => {
    const { t } = useTranslation();
    const checkAutofill = useCheckAutofill();
    const { showWarning } = useMessageDisplay();
    const parentForm = useReactForm();
    const formRef = useRef();
    const listeners = useRef(new Set());
    const childForms = useRef(new Set());

    // Register nested forms only when the parent should validate/reset them.
    const registerDependentForm = useCallback(form => {
        if (!form) {
            return () => { };
        }
        childForms.current.add(form);
        return () => childForms.current.delete(form);
    }, []);

    const checkValidity = useCallback(() => {
        const autofilled = checkAutofill();
        if (autofilled) {
            showWarning(t('autofillWarnTitle'), t('autofillWarnMessage'), { timeout: 20 });
        }
        const controls = formRef.current?.querySelectorAll('input, select, textarea');
        const isValid = Array.from(controls).reduce((valid, control) => {
            if (control.closest('div[data-react-form]') === formRef.current) {
                const controlValid = control.checkValidity();
                return valid && controlValid;
            }
            return valid;
        }, !autofilled);
        let childrenValid = true;
        childForms.current.forEach(form => {
            const valid = form?.checkValidity?.();
            if (!valid) {
                childrenValid = false;
            }
        });
        return isValid && childrenValid;
    }, [checkAutofill, showWarning, t]);

    const onReset = useCallback(fn => {
        listeners.current.add(fn);
        return () => listeners.current.delete(fn);
    }, []);

    const reset = useCallback(() => {
        listeners.current.forEach(fn => fn());
        childForms.current.forEach(form => form?.reset?.());
    }, []);

    useImperativeHandle(ref, () => ({
        checkValidity,
        reset
    }));

    useEffect(() => {
        if (!registerAsChild || !parentForm?.registerDependentForm) {
            return undefined;
        }
        return parentForm.registerDependentForm({ checkValidity, reset });
    }, [parentForm?.registerDependentForm, registerAsChild, checkValidity, reset, parentForm]);

    const value = useMemo(() => ({ onReset, registerDependentForm }), [onReset, registerDependentForm]);

    return <ReactFormContext.Provider value={value}>    
        <div ref={formRef} data-react-form {...props}>
            {children}
        </div>
    </ReactFormContext.Provider>
});

const ReactForm = forwardRef(({ ...props}, ref) => {
    return <FormAutofillProvider>
        <ReactFormImpl ref={ref} {...props}></ReactFormImpl>
    </FormAutofillProvider>
})
export default ReactForm;